import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Globe, Mail, Send, Loader2, Building2 } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { useLang } from "../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO_ICONS = { address: MapPin, branch2: Building2, phone: Phone, website: Globe, email: Mail };

const inputCls =
  "w-full rounded-md border border-[#E8DFC5] bg-[#FBF6E9] px-4 py-3 text-base text-stone-800 placeholder:text-stone-400 outline-none transition-colors duration-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40";



export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: t.contact.options[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.successTitle, {
        description: t.contact.successDesc,
      });
      setForm({ name: "", email: "", phone: "", service: t.contact.options[0], message: "" });
    } catch (err) {
      toast.error(t.contact.errorTitle, {
        description: t.contact.errorDesc,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 lg:py-32 overflow-hidden">
      <div className="absolute inset-x-0 bottom-0 h-[50vh] solar-glow rotate-180 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          index={t.contact.index}
          eyebrow={t.contact.eyebrow}
          title={t.contact.title}
          description={t.contact.desc}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-5">
            <div className="flex flex-col gap-5">
              {t.contact.info.map((item) => { const Icon = INFO_ICONS[item.id]; return (
                <div
                  key={item.id}
                  data-testid={`contact-info-${item.id}`}
                  className="flex items-start gap-4 rounded-lg border border-[#E8DFC5] bg-white p-5 transition-colors duration-300 hover:border-emerald-500/40"
                >
                  <span className="shrink-0 w-11 h-11 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-emerald-600" strokeWidth={1.8} />
                  </span>
                  <div>
                    <p className="text-sm text-stone-500 mb-1">{item.label}</p>
                    <p className="text-base text-stone-800 font-medium" dir={item.dir || undefined}>
                      {item.value}
                    </p>
                  </div>
                </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-lg border border-[#E8DFC5] bg-white p-7 lg:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-stone-500 mb-2">{t.contact.nameLabel}</label>
                  <input
                    id="contact-name"
                    data-testid="contact-form-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder={t.contact.namePh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-sm text-stone-500 mb-2">{t.contact.phoneLabel}</label>
                  <input
                    id="contact-phone"
                    data-testid="contact-form-phone-input"
                    required
                    dir="ltr"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+964 7XX XXX XXXX"
                    className={`${inputCls} text-right`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-stone-500 mb-2">{t.contact.emailLabel}</label>
                  <input
                    id="contact-email"
                    data-testid="contact-form-email-input"
                    type="email"
                    required
                    dir="ltr"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="name@example.com"
                    className={`${inputCls} text-right`}
                  />
                </div>
                <div>
                  <label htmlFor="contact-service" className="block text-sm text-stone-500 mb-2">{t.contact.serviceLabel}</label>
                  <select
                    id="contact-service"
                    data-testid="contact-form-service-select"
                    value={form.service}
                    onChange={set("service")}
                    className={inputCls}
                  >
                    {t.contact.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mb-7">
                <label htmlFor="contact-message" className="block text-sm text-stone-500 mb-2">{t.contact.messageLabel}</label>
                <textarea
                  id="contact-message"
                  data-testid="contact-form-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={t.contact.messagePh}
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={sending}
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-emerald-500 px-9 py-4 font-bold text-white transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_32px_rgba(16,185,129,0.45)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.contact.sending}
                  </>
                ) : (
                  <>
                    {t.contact.send}
                    <Send className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
